import React, { useState } from "react";
import { X, PhoneOutgoing, Check } from "lucide-react";
import { api } from "@/lib/api";

const OUTCOMES = [
  { v: "interested", el: "Ενδιαφερόμενος", en: "Interested" },
  { v: "not_interested", el: "Δεν ενδιαφέρεται", en: "Not interested" },
  { v: "callback_requested", el: "Ζήτησε νέα κλήση", en: "Callback requested" },
  { v: "meeting_scheduled", el: "Κλείστηκε ραντεβού", en: "Meeting scheduled" },
  { v: "proposal_requested", el: "Ζήτησε προσφορά", en: "Proposal requested" },
  { v: "deal_won", el: "Κλείστηκε συμφωνία", en: "Deal won" },
  { v: "no_answer", el: "Δεν απάντησε ξανά", en: "No answer again" },
];

export default function MissedCallReturnModal({ call, L, onClose, onDone }) {
  const [outcome, setOutcome] = useState("callback_requested");
  const [notes, setNotes] = useState("");
  const [duration, setDuration] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.patch(`/calls/${call.id}/mark-returned`, {
        outcome,
        notes: notes || null,
        duration_minutes: duration,
      });
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || (L ? "Σφάλμα αποθήκευσης" : "Save failed"));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-md w-full border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="missed-return-modal"
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              {L ? "Επιστροφή κλήσης" : "Returning missed call"}
            </div>
            <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">
              {call.missed_from_number || call.phone_number_called || "—"}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-6 space-y-4">
          {/* Outcome selector */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">
              {L ? "Αποτέλεσμα κλήσης" : "Call outcome"}
            </label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {OUTCOMES.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`missed-outcome-${o.v}`}
                  onClick={() => setOutcome(o.v)}
                  className={`text-left px-3 py-2 rounded-md border text-xs font-semibold transition-colors ${
                    outcome === o.v
                      ? "bg-[#0052CC] text-white border-[#0052CC]"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {outcome === o.v && <Check className="inline w-3 h-3 mr-1 -mt-0.5" />}
                  {L ? o.el : o.en}
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">
              {L ? "Διάρκεια (λεπτά)" : "Duration (minutes)"}
            </label>
            <input
              data-testid="missed-duration"
              type="number"
              min="0"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value || "0", 10))}
              className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">
              {L ? "Σημειώσεις (τι είπαμε)" : "Notes (what was discussed)"}
            </label>
            <textarea
              data-testid="missed-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder={L ? "π.χ. Ενδιαφέρεται για κατάλογο ελαιολάδου, στέλνουμε τιμοκατάλογο..." : "e.g. Interested in the olive oil catalogue, sending pricing..."}
              className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none"
            />
          </div>

          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">{err}</div>}
        </div>

        <footer className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50/50">
          <button
            data-testid="missed-cancel"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md"
          >
            {L ? "Άκυρο" : "Cancel"}
          </button>
          <button
            data-testid="missed-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0052CC] hover:bg-[#0043A8] text-white text-sm font-semibold rounded-md disabled:opacity-50 shadow-sm"
          >
            <PhoneOutgoing className="w-4 h-4" />
            {saving ? (L ? "Αποθήκευση..." : "Saving...") : (L ? "Καταχώρηση στο ιστορικό" : "Log to history")}
          </button>
        </footer>
      </div>
    </div>
  );
}
